import React, { useState, useEffect } from 'react';
import styles from '../../app.module.css';
const SchemaForm = ({ initialData, onSave, onCancel, existingSchemas = [] }) => {
    const [schema, setSchema] = useState({
        name: '',
        type: '',
        required: [],
        description: '',
        properties: {},
        items: { type: '', format: '', $ref: '' },
    });

    const [savedSchema, setSavedSchema] = useState(null);

    const [popupMessage, setPopupMessage] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const isEditing = !!initialData;

    const showPopupModal = (message) => {
        setPopupMessage(message);
        setShowPopup(true);
    };

    const formatOptions = {
        number: ['float', 'double'],
        integer: ['int32', 'int64'],
        string: ['date', 'date-time', 'email', 'uuid'],
        boolean: [],
    };

    useEffect(() => {
        if (initialData) {
            setSchema({
                name: initialData.name || '',
                type: initialData.type || '',
                required: initialData.required || [],
                description: initialData.description || '',
                properties: initialData.properties || {},
                items: initialData.items || { type: '', format: '', $ref: '' },
            });
        } else {
            setSchema({
                name: '',
                type: '',
                required: [],
                description: '',
                properties: {},
                items: { type: '', format: '', $ref: '' },
            });
        }
    }, [initialData]);

    const handleInputChange = (field, value) => {
        setSchema((prev) => ({ ...prev, [field]: value }));
    };

    const handleArrayItemsTypeChange = (value) => {
        setSchema((prev) => {
            const updatedItems = { ...prev.items };
            if (existingSchemas.includes(value)) {
                updatedItems.$ref = `#/components/schemas/${value}`;
                updatedItems.type = '';
                updatedItems.format = '';
            } else if (['string', 'boolean', 'integer', 'number'].includes(value)) {
                updatedItems.type = value;
                updatedItems.$ref = '';
                updatedItems.format = '';
            } else {
                updatedItems.type = '';
                updatedItems.$ref = '';
                updatedItems.format = '';
            }
            return { ...prev, items: updatedItems };
        });
    };

    const handleArrayItemsFormatChange = (value) => {
        setSchema((prev) => {
            const updatedItems = { ...prev.items };
            updatedItems.format = value;
            return { ...prev, items: updatedItems };
        });
    };

    // const handleAddProperty = () => {
    //     const newPropertyKey = `property_${Object.keys(schema.properties).length + 1}`;
    //     setSchema((prev) => ({
    //         ...prev,
    //         properties: {
    //             ...prev.properties,
    //             [newPropertyKey]: { name: '', type: '', description: '', example: '' },
    //         },
    //     }));
    // };

    const handleAddProperty = () => {
        const newPropertyKey = `property_${Object.keys(schema.properties).length + 1}`;
        setSchema((prev) => ({
            ...prev,
            properties: {
                ...prev.properties,
                [newPropertyKey]: { name: '', type: '', description: '', example: '' },
            },
        }));
    };


    const handlePropertyChange = (propertyKey, field, value) => {
        setSchema((prev) => {
            const updatedProperty = { ...prev.properties[propertyKey] };

            if (field === 'type' && existingSchemas.includes(value)) {
                updatedProperty.$ref = `#/components/schemas/${value}`;
                delete updatedProperty.type;
                delete updatedProperty.items;
                delete updatedProperty.format;
                updatedProperty.description = updatedProperty.description || '';
                updatedProperty.example = updatedProperty.example || '';
            }

            else if (field === 'type') {

                updatedProperty.type = value;
                delete updatedProperty.$ref;

                if (value === 'array') {
                    updatedProperty.items = updatedProperty.items || {};
                } else {
                    delete updatedProperty.items;
                }
            }
            else {
                updatedProperty[field] = value;
            }

            return {
                ...prev,
                properties: {
                    ...prev.properties,
                    [propertyKey]: updatedProperty,
                },
            };
        });
    };

    const handleRequiredChange = (propertyKey, isChecked) => {
        setSchema((prev) => {
            const requiredFields = new Set(prev.required);
            if (isChecked) {
                requiredFields.add(prev.properties[propertyKey].name || propertyKey);
            } else {
                requiredFields.delete(prev.properties[propertyKey].name || propertyKey);
            }
            return {
                ...prev,
                required: Array.from(requiredFields),
            };
        });
    };

    const handleRemoveProperty = (propertyKey) => {
        setSchema((prev) => {
            const updatedProps = { ...prev.properties };
            delete updatedProps[propertyKey];
            return {
                ...prev,
                properties: updatedProps,
                required: prev.required.filter((k) => k !== propertyKey),
            };
        });
    };

    const findDuplicates = (array) => {
        const counts = {};
        const duplicates = [];

        array.forEach((item) => {
            counts[item] = (counts[item] || 0) + 1;
        });

        Object.entries(counts).forEach(([name, count]) => {
            if (count > 1) {
                duplicates.push(name);
            }
        });

        return duplicates;
    };

    const handleSubmit = () => {
        if (!schema.name || !schema.type) {
            showPopupModal('Name and Type are required.');
            return;
        }

        if (!isEditing && existingSchemas.includes(schema.name)) {
            showPopupModal(`A schema named "${schema.name}" already exists.`);
            return;
        }

        const { name, type, description, required, properties, items } = schema;
        const schemaToSave = { name, type };

        if (description && description.trim()) {
            schemaToSave.description = description;
        }

        if (type === 'array') {
            if (!items.$ref && !items.type) {
                showPopupModal('Please specify the array item type or reference.');
                return;
            }
            schemaToSave.items = {};
            if (items.$ref) {
                schemaToSave.items.$ref = items.$ref;
            } else {
                schemaToSave.items.type = items.type;
                if (items.format && items.format.trim()) {
                    schemaToSave.items.format = items.format;
                }
            }
        }
        else if (type === 'object') {
            const propertyNames = Object.entries(properties).map(([key, prop]) => prop.name || key);
            const duplicates = findDuplicates(propertyNames);
            if (duplicates.length > 0) {
                showPopupModal(`Duplicate property names found: ${duplicates.join(', ')}`);
                return;
            };

            const propertiesWithNames = Object.fromEntries(
                Object.entries(properties).map(([key, prop]) => {
                    const {
                        name: propName,
                        $ref,
                        format,
                        description: propDesc,
                        example,
                        items: propItems,
                        type: propType,
                        ...rest
                    } = prop;

                    const finalProp = { ...rest };

                    if ($ref) {
                        finalProp.$ref = $ref;

                    } else if (propType) {

                        finalProp.type = propType;

                        if (format && format.trim()) {
                            finalProp.format = format;
                        }

                        if (propType === 'array' && propItems) {
                            finalProp.items = {};
                            if (propItems.$ref) {
                                finalProp.items.$ref = propItems.$ref;
                            } else if (propItems.type) {
                                finalProp.items.type = propItems.type;
                                if (propItems.format && propItems.format.trim()) {
                                    finalProp.items.format = propItems.format;
                                }
                            }
                        }
                    }

                    if (propDesc && propDesc.trim()) {
                        finalProp.description = propDesc;
                    }

                    if (example && example.trim()) {
                        finalProp.example = example;
                    }

                    return [propName || key, finalProp];
                })
            );
            schemaToSave.properties = propertiesWithNames;
            if (required && required.length > 0) {
                schemaToSave.required = required;
            }
        }

        onSave(name, schemaToSave);

        setSavedSchema(schemaToSave);
    };

    if (savedSchema) {
        const { name, type, description, items } = savedSchema;
        return (
            <div className={styles.style2}>
                <h3>Schema Saved!</h3>
                <p><strong>Name:</strong> {name}</p>
                <p><strong>Schema Type:</strong> {type}</p>
                <p><strong>Description:</strong> {description || 'N/A'}</p>


                {type === 'array' && items && (
                    <p>
                        <strong>Items:</strong>{' '}
                        {items.$ref
                            ? items.$ref.replace('#/components/schemas/', '')
                            : items.type || 'N/A'}
                    </p>
                )}

                <button className={styles.style4} onClick={onCancel}>
                    Close
                </button>
            </div>
        );
    }

    return (
        <div className={styles.mainContainer}>
            <div className={styles.style2}>
                <h3>{isEditing ? 'Edit Schema' : 'Add Schema'}</h3>

                <div className={styles.style3}>
                    <label>Name:</label>
                    <input
                        type="text"
                        value={schema.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        readOnly={isEditing}
                        className={styles.style1}
                    />
                </div>

                <div className={styles.style3}>
                    <label>Type:</label>
                    <select
                        value={schema.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className={styles.style1}
                    >
                        <option value="">Select Type</option>
                        <option value="object">Object</option>
                        <option value="array">Array</option>
                        <option value="boolean">Boolean</option>
                        <option value="integer">Integer</option>
                        <option value="number">Number</option>
                        <option value="string">String</option>
                    </select>
                </div>

                <div className={styles.style3}>
                    <label>Description:</label>
                    <input
                        type="text"
                        value={schema.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className={styles.style1}
                    />
                </div>

                {schema.type === 'array' && (
                    <div className={styles.style3}>
                        <h4>Array Items</h4>
                        <p>Choose either a basic data type or an existing schema reference:</p>
                        <select
                            value={
                                schema.items.$ref
                                    ? schema.items.$ref.replace('#/components/schemas/', '')
                                    : schema.items.type
                            }
                            onChange={(e) => handleArrayItemsTypeChange(e.target.value)}
                            className={styles.style1}
                        >
                            <option value="">-- Select Items Type or Schema --</option>
                            <option value="string">string</option>
                            <option value="integer">integer</option>
                            <option value="number">number</option>
                            <option value="boolean">boolean</option>
                            {existingSchemas.map((schemaName) => (
                                <option key={schemaName} value={schemaName}>
                                    {schemaName}
                                </option>
                            ))}
                        </select>

                        {schema.items.type && !schema.items.$ref && (
                            <div>
                                <label>Format (optional):</label>
                                <select
                                    value={schema.items.format || ''}
                                    onChange={(e) => handleArrayItemsFormatChange(e.target.value)}
                                    className={styles.style1}
                                >
                                    <option value="">-- No Format --</option>
                                    {formatOptions[schema.items.type]?.map((fmt) => (
                                        <option key={fmt} value={fmt}>
                                            {fmt}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {schema.type === 'object' && (
                    <div>
                        <h4>Properties</h4>
                        {Object.entries(schema.properties).map(([key, property]) => (
                            <div
                                key={key}
                                className={styles.multiInputFormContainer}
                            >
                                <input
                                    type="text"
                                    placeholder="Enter property name"
                                    value={property.name}
                                    onChange={(e) => handlePropertyChange(key, 'name', e.target.value)}
                                    className={styles.multiInputForm}
                                />
                                <select
                                    value={
                                        property.$ref
                                            ? property.$ref.replace('#/components/schemas/', '')
                                            : property.type || ''
                                    }
                                    onChange={(e) => handlePropertyChange(key, 'type', e.target.value)}
                                    className={styles.multiInputForm}
                                >
                                    <option value="">Select Type</option>
                                    <option value="array">Array</option>
                                    <option value="boolean">Boolean</option>
                                    <option value="integer">Integer</option>
                                    <option value="number">Number</option>
                                    <option value="string">String</option>
                                    {existingSchemas.map((schemaName) => (
                                        <option key={schemaName} value={schemaName}>
                                            {schemaName}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={
                                        property.type === 'array'
                                            ? property.items?.$ref
                                                ? property.items.$ref.replace('#/components/schemas/', '')
                                                : property.items?.type || ''
                                            : property.format || ''
                                    }
                                    onChange={(e) => {
                                        if (property.type === 'array') {
                                            const picked = e.target.value;
                                            if (existingSchemas.includes(picked)) {
                                                handlePropertyChange(key, 'items', {
                                                    $ref: `#/components/schemas/${picked}`
                                                });
                                            } else if (['string', 'boolean', 'integer', 'number'].includes(picked)) {
                                                handlePropertyChange(key, 'items', { type: picked });
                                            } else {
                                                handlePropertyChange(key, 'items', {});
                                            }
                                        } else {
                                            handlePropertyChange(key, 'format', e.target.value);
                                        }
                                    }}
                                    disabled={
                                        property.type === 'array'
                                            ? false
                                            : !formatOptions[property.type]?.length || property.type === ''
                                    }
                                    style={{
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        backgroundColor:
                                            property.type === 'array' ||
                                            (formatOptions[property.type]?.length
                                                ? 'white'
                                                : '#f0f0f0'),
                                    }}
                                >
                                    <option value="">
                                        {property.type === 'array' ? 'Select Schema' : 'Select Format'}
                                    </option>
                                    {property.type === 'array'
                                        ? (
                                            <>
                                                {['string', 'integer', 'number', 'boolean'].map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                                {existingSchemas.map((schemaName) => (
                                                    <option key={schemaName} value={schemaName}>
                                                        {schemaName}
                                                    </option>
                                                ))}
                                            </>
                                        )
                                        : (
                                            formatOptions[property.type]?.map((fmt) => (
                                                <option key={fmt} value={fmt}>{fmt}</option>
                                            ))
                                        )
                                    }
                                </select>
                                <input
                                    type="text"
                                    placeholder="Description"
                                    value={property.description || ''}
                                    disabled={!!property.$ref}
                                    onChange={(e) => handlePropertyChange(key, 'description', e.target.value)}
                                    className={styles.multiInputForm}
                                />
                                <input
                                    type="text"
                                    placeholder="Example"
                                    value={property.example || ''}
                                    disabled={!!property.$ref}
                                    onChange={(e) => handlePropertyChange(key, 'example', e.target.value)}
                                    className={styles.multiInputForm}
                                />

                                <div className={styles.style5}>
                                    <label className={styles.reqStyle}>Req</label>
                                    <input
                                        type="checkbox"
                                        checked={schema.required.includes(property.name || key)}
                                        onChange={(e) => handleRequiredChange(key, e.target.checked)}
                                        style={{ margin: 0 }}
                                    />
                                    <button
                                        className={styles.removeIcon}
                                        onClick={() => handleRemoveProperty(key)}
                                    >
                                        X
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            className={styles.propertyButton}
                            onClick={handleAddProperty}
                        >
                            + Add Property
                        </button>
                    </div>
                )}

                <div className={styles.sNcButtonContainer}>
                    <button
                        className={styles.saveButton}
                        onClick={handleSubmit}
                    >
                        {isEditing ? 'Save Changes' : 'Add Schema'}
                    </button>
                    <button
                        className={styles.cancelButton}
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                </div>

                {showPopup && (
                    <div className={styles.popupOverlay}>
                        <div className={styles.popup}>
                            <h3>Error</h3>
                            <p>{popupMessage}</p>
                            <button
                                onClick={() => setShowPopup(false)}
                                className="popup-close-button"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchemaForm;
